import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ResponsivePie } from '@nivo/pie';
import dayjs from 'dayjs';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  toCurrencyString,
  toNumberString,
  toPercent,
  useRouteDocumentTitle,
} from '@client/toolkit';
import type { CurrencyCode } from '@client/toolkit';

import { DateRangeFilter } from '../components/date-range-filter';
import { DarkHorizontalScroll } from '../components/dark-horizontal-scroll';
import { dateRangeOptions } from '../utils/date-range-options';

import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import {
  PortfolioDiversificationRow,
  useGetReportingPortfolioDiversificationQuery,
} from '@client/api';

const PIE_COLORS = ['#7DC3F0', '#5092C2', '#49B283', '#CADDF4', '#EB7370', '#F2B05C', '#9D8DF1', '#3FB6AB'];

const PLACEHOLDER = '\u2013';

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  return toNumberString(value, PLACEHOLDER, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatCurrency(value: number | null | undefined, currency: string): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  return toCurrencyString(value, PLACEHOLDER, { currency: currency as CurrencyCode, maximumFractionDigits: 0 });
}

function formatPercent(value: number | null | undefined, fractionDigits = 1): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  // API returns percentage values (e.g. 75 for 75%); toPercent expects a fraction.
  return toPercent(value / 100, PLACEHOLDER, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

type Variant = 'building' | 'tenant';

function CompositionCard({
  title,
  variant,
  rows,
  loading,
  currency,
  countHeader,
  keyHeader,
}: {
  title: string;
  variant: Variant;
  rows: PortfolioDiversificationRow[];
  loading: boolean;
  currency: string;
  countHeader: string;
  keyHeader: string;
}) {
  const { t } = useTranslation('reporting');
  // Only show rows with actual income. A building/tenant with no rental
  // income in the selected period should not appear (and the donut should
  // not render an all-zero slice). When nothing has income the card renders
  // an "Ingen data" empty state.
  const visibleRows = useMemo(() => rows.filter((r) => r.yearlyIncome > 0), [rows]);

  const pieData = useMemo(
    () =>
      visibleRows.map((d, idx) => ({
        id: d.key,
        label: d.key,
        value: Number(d.yearlyIncome.toFixed(0)),
        color: PIE_COLORS[idx % PIE_COLORS.length],
        percentage: d.percentage ?? null,
      })),
    [visibleRows],
  );

  const colSpan = variant === 'tenant' ? 4 : 5;

  return (
    <Card className="flex-1 min-w-[420px]">
      <CardHeader>
        <CardTitle variant="highlighted">{title}</CardTitle>
      </CardHeader>
      <CardContent variant="fill" className="px-4 pb-4">
        <div className="h-44 mb-4">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="w-12 h-12 border-2 border-brand-primary rounded-full animate-spin border-r-transparent" />
            </div>
          ) : pieData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-white/60 text-sm">{t('portfolioDiversification.noData')}</div>
          ) : (
            <ResponsivePie
              data={pieData}
              margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
              innerRadius={0.65}
              padAngle={1}
              cornerRadius={2}
              colors={(d) => (d.data as { color: string }).color}
              enableArcLabels={false}
              enableArcLinkLabels={false}
              borderWidth={0}
              isInteractive
              tooltip={({ datum }) => (
                <div className="bg-surface-elevated text-white rounded px-2 py-1 text-xs shadow">
                  {datum.label}: {formatCurrency(datum.value, currency)} ({formatPercent(datum.data.percentage)})
                </div>
              )}
            />
          )}
        </div>
        <DarkHorizontalScroll>
        <Table className="w-full" size="small">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{keyHeader}</TableHeadCell>
              <TableHeadCell align="right">{countHeader}</TableHeadCell>
              {variant === 'building' ? <TableHeadCell align="right">{t('portfolioDiversification.header.totalArea')}</TableHeadCell> : null}
              <TableHeadCell align="right">{t('portfolioDiversification.header.yearlyIncome')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioDiversification.header.share')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from(Array(4).keys()).map((x) => (
                  <TableRow key={x}>
                    {Array.from(Array(colSpan).keys()).map((y) => (
                      <TableCell key={y} loading />
                    ))}
                  </TableRow>
                ))
              : visibleRows.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={colSpan}>
                      <span className="text-white/60">{t('portfolioDiversification.noData')}</span>
                    </TableCell>
                  </TableRow>
                )
                : visibleRows.map((row, idx) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        <span className="flex items-center gap-2 truncate">
                          <span
                            className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                            style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                          />
                          <span className="truncate">{row.key}</span>
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(variant === 'tenant' ? row.tenantCount : row.buildingCount)}
                      </TableCell>
                      {variant === 'building' ? (
                        <TableCell align="right">{formatNumber(row.areaM2)}</TableCell>
                      ) : null}
                      <TableCell align="right">{formatCurrency(row.yearlyIncome, currency)}</TableCell>
                      <TableCell align="right">{formatPercent(row.percentage)}</TableCell>
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
        </DarkHorizontalScroll>
      </CardContent>
    </Card>
  );
}

export function ReportingPortfolioDiversificationRoute() {
  const { t: tType } = useTranslation('type');
  const { t: tReporting } = useTranslation('reporting');
  const [searchParams] = useSearchParams();
  useRouteDocumentTitle(tReporting('portfolioDiversification.documentTitle'), ReportingPaths.PortfolioDiversification);

  // `?view=` splits this report between the two sidebar entries: `geography` shows only the
  // geography card, `segment` shows segment + industry; no param shows all three.
  const view = searchParams.get('view');
  const showGeography = view !== 'segment';
  const showSegment = view !== 'geography';

  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const initialStartDate = startDateParam ? dayjs(startDateParam).toDate() : new Date(new Date().getFullYear(), 0, 1);
  const initialEndDate = endDateParam ? dayjs(endDateParam).toDate() : new Date(new Date().getFullYear(), 11, 31);

  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  const { data, isLoading } = useGetReportingPortfolioDiversificationQuery({
    startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
    endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
  });

  const currency = data?.currency ?? 'NOK';

  const byCity = data?.byCity ?? [];
  const byType = useMemo(
    () =>
      (data?.byType ?? []).map((row) => ({
        ...row,
        key: tType(`BuildingType.${row.key}`, { defaultValue: row.key }),
      })),
    [data?.byType, tType],
  );
  const byIndustry = useMemo(
    () =>
      (data?.byIndustry ?? []).map((row) => ({
        ...row,
        key: tReporting(`portfolioDiversification.industrySections.${row.key}`, { defaultValue: row.key }),
      })),
    [data?.byIndustry, tReporting],
  );

  return (
    <ReportPageContent
      breadcrumb={tReporting('portfolioDiversification.breadcrumb')}
      menu={
        <DateRangeFilter
          options={[
            dateRangeOptions().thisYear,
            dateRangeOptions().previousYear,
            dateRangeOptions().custom,
          ]}
          pickerMode="year"
          onChange={(d) => setTimeRange({ startDate: d[0], endDate: d[1] })}
          initialStartDate={timeRange.startDate}
          initialEndDate={timeRange.endDate}
        />
      }
    >
      <div className="flex gap-5 flex-wrap">
        {showGeography ? (
          <CompositionCard
            title={tReporting('portfolioDiversification.cards.geography')}
            variant="building"
            rows={byCity}
            loading={isLoading}
            currency={currency}
            keyHeader={tReporting('portfolioDiversification.header.municipality')}
            countHeader={tReporting('portfolioDiversification.header.buildings')}
          />
        ) : null}
        {showSegment ? (
          <CompositionCard
            title={tReporting('portfolioDiversification.cards.segment')}
            variant="building"
            rows={byType}
            loading={isLoading}
            currency={currency}
            keyHeader={tReporting('portfolioDiversification.header.type')}
            countHeader={tReporting('portfolioDiversification.header.buildings')}
          />
        ) : null}
        {showSegment ? (
          <CompositionCard
            title={tReporting('portfolioDiversification.cards.industry')}
            variant="tenant"
            rows={byIndustry}
            loading={isLoading}
            currency={currency}
            keyHeader={tReporting('portfolioDiversification.header.industry')}
            countHeader={tReporting('portfolioDiversification.header.tenants')}
          />
        ) : null}
      </div>
    </ReportPageContent>
  );
}
